import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaCalendar, FaUser, FaTag, FaShare } from 'react-icons/fa';
import axios from 'axios';

const API_URL = 'https://aamirsaba.com/api';

const BlogDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // LOAD blog post from API
  useEffect(() => {
    axios.get(`${API_URL}/blog/${id}`)
      .then(response => {
        console.log('Blog post loaded:', response.data);
        setPost(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading blog post:', error);
        setError('Post not found');
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <section className="pt-32 pb-20 bg-navy min-h-screen">
        <div className="container mx-auto px-6 text-center">
          <div className="text-cyan text-xl">Loading...</div>
        </div>
      </section>
    );
  }

  if (error || !post) {
    return (
      <section className="pt-32 pb-20 bg-navy min-h-screen">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold text-white">Article Not Found</h1>
          <p className="text-gray-400 mt-4">The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="inline-block mt-6 text-cyan hover:underline">← Back to Blog</Link>
        </div>
      </section>
    );
  }

  // Format content - replace newlines with <br> tags if content is plain text
  const formatContent = (content) => {
    if (!content) return '<p>No content available.</p>';
    // If content has HTML tags, render as is
    if (content.includes('<') && content.includes('>')) {
      return content;
    }
    // Otherwise, convert newlines to <br> and wrap in paragraphs
    return content.split('\n').filter(line => line.trim()).map(line => 
      `<p>${line}</p>`
    ).join('');
  };

  return (
    <section className="pt-32 pb-20 bg-navy min-h-screen">
      <div className="container mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
          <Link to="/blog" className="text-cyan hover:underline inline-flex items-center gap-2">
            <FaArrowLeft /> Back to Blog
          </Link>

          <div className="mt-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs text-cyan uppercase tracking-wider flex items-center gap-1">
                <FaTag /> {post.category || 'General'}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">{post.title}</h1>
            <div className="flex items-center gap-4 mt-4 text-sm text-gray-400">
              <span className="flex items-center gap-1"><FaCalendar /> {post.date || post.createdAt?.split('T')[0] || 'Recent'}</span>
              <span className="flex items-center gap-1"><FaUser /> {post.author || 'Admin'}</span>
            </div>
          </div>

          <div className="mt-8 bg-darkNavy rounded-xl p-8 border border-gray-800">
            <div className="prose prose-invert max-w-none text-gray-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatContent(post.content) }}
            />
          </div>

          <div className="mt-8 flex items-center justify-between">
            <Link to="/blog" className="text-cyan hover:underline">← Back to all articles</Link>
            <div className="flex items-center gap-3">
              <span className="text-gray-400 text-sm">Share:</span>
              <button 
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: post.title,
                      text: post.excerpt,
                      url: window.location.href
                    });
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied to clipboard!');
                  }
                }}
                className="text-gray-400 hover:text-cyan transition"
              >
                <FaShare />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogDetail;